/**
 * Shared chart components used by both Cell Access and Scraping Detection views.
 * Extracted to avoid duplication between the two analytics sub-views.
 */
import * as echarts from 'echarts'
import { useEffect, useRef, useState } from 'preact/hooks'
import { getCellAccessTimeline } from '../../../../services/adminService.js'
import { COLORS, chartTheme } from './chartTheme.js'

// Suspicion flag human-readable descriptions
export const FLAG_DESCRIPTIONS = {
  low_hour_variance: 'Requests concentrated in few hours (scripted pattern)',
  high_no_referer_ratio: 'Most full data requests lack HTTP Referer header',
  high_request_volume: 'Over 100 full cell data requests in the period',
  high_coverage: 'Accessed more than 50% of all available cells',
  bot_user_agent: 'User-Agent matched known bot/scraper pattern',
  high_daily_rate: 'Over 30 full data requests per active day',
  multi_ip: 'Accessing from 5+ distinct IP addresses',
  no_explorer_usage: 'Full requests without ever using explorer/metadata',
  sequential_access: 'Cells accessed in sequential ID order (scripted)',
  high_burst_rate: 'Over 20 unique cells in a 10-minute window',
  single_session_scrape: 'Over 50 unique cells in a single day with no repeats',
  no_interaction_depth: 'Full cell requests but no section navigation',
}

// A row is "mostly refused" once at least this share of its attempted full-data
// requests was blocked (429 / 401 / 403). Kept here so the cell-access table,
// the scraping ranking and the per-user detail panel all draw the same line.
export const REFUSED_SHARE_THRESHOLD = 0.5

/** Share of attempted full-data requests that was refused (0..1, 0 when no attempts) */
export function refusedShare(attempted, blocked) {
  const att = attempted ?? 0
  const blk = blocked ?? 0
  if (att <= 0) return 0
  return Math.min(1, blk / att)
}

/**
 * Marker for a user whose full-data requests were mostly refused.
 * Without it a throttled scraper renders as "Full Requests 0" next to a
 * critical suspicion score, i.e. as a broken score rather than a blocked abuser.
 */
export function RefusedBadge({ attempted, blocked }) {
  const share = refusedShare(attempted, blocked)
  if ((attempted ?? 0) <= 0 || share < REFUSED_SHARE_THRESHOLD) return null
  const pct = Math.round(share * 100)
  return (
    <span
      className="admin-badge admin-badge--error"
      style={{ marginTop: '4px', alignSelf: 'flex-start' }}
      title={`${pct}% of ${attempted} attempted full-data requests were refused (429/401/403). The served volume is low because the requests were blocked, not because the account was idle.`}
    >
      {pct}% refused
    </span>
  )
}

/** Blocked-request count, highlighted once refusals dominate the attempts */
export function BlockedCount({ attempted, blocked }) {
  const blk = blocked ?? 0
  const hot = refusedShare(attempted, blocked) >= REFUSED_SHARE_THRESHOLD && blk > 0
  return (
    <span style={{ color: hot ? '#ef4444' : COLORS.gray, fontWeight: hot ? 600 : 400 }}>
      {blk}
    </span>
  )
}

/** Score badge with color coding */
export function SuspicionBadge({ score }) {
  let className = 'admin-badge--success'
  if (score >= 71) className = 'admin-badge--error'
  else if (score >= 46) className = 'admin-badge--warning'
  else if (score >= 26) className = 'admin-badge--warning'

  let badgeStyle = {}
  if (score >= 46 && score < 71) {
    badgeStyle = { background: '#ea5b0c', color: '#fff' }
  }

  return (
    <span className={`admin-badge ${className}`} style={badgeStyle}>
      {score}
    </span>
  )
}

/** Tier badge */
export function TierBadge({ tier }) {
  if (!tier) return <span className="admin-badge">-</span>
  const cls =
    tier === 'PRO'
      ? 'admin-badge--success'
      : tier === 'DEMO'
        ? 'admin-badge--warning'
        : ''
  return <span className={`admin-badge ${cls}`}>{tier}</span>
}

/** Inline ECharts bar chart for user timeline (hourly buckets) */
export function TimelineChart({ data, height = 220 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const xData = data.map((d) => {
      const dt = new Date(d.hour)
      return `${dt.getMonth() + 1}/${dt.getDate()} ${dt.getHours()}h`
    })

    const option = {
      ...chartTheme,
      grid: { left: 50, right: 20, top: 20, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        {
          name: 'Full Requests',
          type: 'bar',
          stack: 'total',
          data: data.map((d) => d.full_requests),
          itemStyle: { color: COLORS.orange, borderRadius: [0, 0, 0, 0] },
        },
        {
          name: 'Batch Curves',
          type: 'bar',
          stack: 'total',
          data: data.map((d) => d.batch_curve_requests || 0),
          itemStyle: { color: '#8b5cf6', borderRadius: [0, 0, 0, 0] },
        },
        {
          name: 'Light Requests',
          type: 'bar',
          stack: 'total',
          data: data.map((d) => d.light_requests),
          itemStyle: { color: COLORS.cyan, borderRadius: [2, 2, 0, 0] },
        },
        {
          // Refused attempts (429/401/403). Deliberately its OWN stack: these
          // were never served, so folding them into the served total would
          // overstate delivered data.
          name: 'Blocked',
          type: 'bar',
          stack: 'blocked',
          data: data.map((d) => d.blocked_requests || 0),
          itemStyle: { color: '#ef4444', borderRadius: [2, 2, 0, 0] },
        },
      ],
      legend: {
        ...chartTheme.legend,
        right: 0,
        top: 0,
        data: ['Full Requests', 'Batch Curves', 'Light Requests', 'Blocked'],
      },
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <p>No timeline data</p>
      </div>
    )
  }

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Access Timeline</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/** Hourly pattern chart (24 bars, one per hour UTC) */
export function HourlyPatternChart({ data, height = 180 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const option = {
      ...chartTheme,
      grid: { left: 40, right: 20, top: 10, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        formatter: (params) =>
          `${params[0].axisValue}:00 UTC: ${params[0].value} Requests`,
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => `${i}`),
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, formatter: (v) => `${v}h` },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        {
          type: 'bar',
          data: data,
          barWidth: '70%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: COLORS.cyan },
              { offset: 1, color: COLORS.cyan + '60' },
            ]),
            borderRadius: [3, 3, 0, 0],
          },
        },
      ],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data || data.every((v) => v === 0)) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <p>No data</p>
      </div>
    )
  }

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Hourly Pattern (UTC)</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/** Expandable detail panel for a user row (timeline + hourly + accessed cells) */
export function UserDetailPanel({ userId, periodDays }) {
  const [timeline, setTimeline] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getCellAccessTimeline(userId, periodDays)
      .then((data) => { if (!cancelled) setTimeline(data) })
      .catch((err) => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [userId, periodDays])

  if (loading) {
    return (
      <div className="cell-access-detail-panel">
        <div className="analytics-loading" style={{ padding: '20px' }}>
          <div className="spinner"></div>
          <p>Loading details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="cell-access-detail-panel">
        <div className="analytics-error" style={{ padding: '20px' }}>
          <p>Error: {error}</p>
        </div>
      </div>
    )
  }

  if (!timeline) return null

  // Rank cells by TOTAL touches (served + refused) before truncating to 50.
  // Sorting by served count alone pushed a cell that was only ever requested and
  // refused (access_count 0) past the cut-off — which is exactly the
  // enumeration walk this panel exists to expose.
  const accessedCells = [...(timeline.accessed_cells || [])].sort(
    (a, b) =>
      (b.access_count || 0) + (b.blocked_count || 0) -
      ((a.access_count || 0) + (a.blocked_count || 0))
  )
  const totalBlockedCells = accessedCells.reduce((s, c) => s + (c.blocked_count || 0), 0)

  return (
    <div className="cell-access-detail-panel">
      <div className="cell-access-detail-charts">
        <TimelineChart data={timeline.timeline} height={220} />
        <HourlyPatternChart data={timeline.hourly_pattern} height={180} />
      </div>

      {accessedCells.length > 0 && (
        <div className="cell-access-detail-cells">
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
            Accessed Cells ({accessedCells.length})
            {totalBlockedCells > 0 && (
              <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: 400, marginLeft: '8px' }}>
                &mdash; {totalBlockedCells} refused request{totalBlockedCells === 1 ? '' : 's'}
              </span>
            )}
          </h4>
          <div className="cell-access-cells-scroll">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Cell</th>
                  <th title="Full-data requests that were actually served.">Access Count</th>
                  <th title="Requests for this cell that were refused (429 rate limit / 401 / 403).">
                    Blocked
                  </th>
                  <th>Last Accessed</th>
                </tr>
              </thead>
              <tbody>
                {accessedCells.slice(0, 50).map((cell) => (
                  <tr key={cell.product_id}>
                    <td style={{ fontSize: '12px' }}>
                      {cell.cell_name || cell.product_id}
                    </td>
                    <td className="count-cell">{cell.access_count}</td>
                    <td className="count-cell">
                      <BlockedCount
                        attempted={(cell.access_count || 0) + (cell.blocked_count || 0)}
                        blocked={cell.blocked_count}
                      />
                    </td>
                    <td>
                      {cell.last_accessed
                        ? new Date(cell.last_accessed).toLocaleDateString('de-DE')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
